import React, { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Check, Clock, UserPlus, Undo2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { updateEntry } from "../lib/api";
import { deleteWithUndo } from "../lib/items";
import { snoozeTargets } from "../lib/resurface";
import { Sheet, SheetContent } from "./ui/sheet";
import { useIsMobile } from "../hooks/useIsMobile";

const tzOffset = () => -new Date().getTimezoneOffset();

// Compact actions for an item so it's editable/actionable wherever it appears,
// without hunting for it in Inbox.
export default function ItemActionsMenu({ entry, onUpdated, align = "right" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (okMsg) toast.success(okMsg);
      onUpdated && onUpdated(res);
    } catch (_) {
      toast.error("Couldn't do that. Try again.");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const done = entry.status === "DONE";
  const targets = snoozeTargets();

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        data-testid={`item-actions-${entry.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="h-8 w-8 rounded-full flex items-center justify-center text-[var(--ink4)] hover:text-[var(--ink)] hover:bg-[var(--line2)] transition-colors"
        title="Actions"
        aria-label="Item actions"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <MoreHorizontal size={18} strokeWidth={2} />}
      </button>

      {open && !isMobile && (
        <div
          className={`absolute z-30 mt-1 w-52 rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_12px_34px_rgba(44,40,37,0.16)] p-1.5 ${align === "right" ? "right-0" : "left-0"}`}
          data-testid={`item-menu-${entry.id}`}
        >
          <button
            data-testid={`action-done-${entry.id}`}
            onClick={() => run(() => updateEntry(entry.id, { status: done ? "ACTIVE" : "DONE" }), done ? "Reopened." : "Done. One less thing to hold.")}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-[var(--ink)] hover:bg-[var(--surface2)] transition-colors"
          >
            {done ? <Undo2 size={15} /> : <Check size={15} />}
            {done ? "Reopen" : "Mark done"}
          </button>

          <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-[var(--ink5)] flex items-center gap-1.5">
            <Clock size={12} /> Snooze
          </div>
          {targets.map((t) => (
            <button
              key={t.key}
              data-testid={`action-snooze-${t.key}-${entry.id}`}
              onClick={() => run(() => updateEntry(entry.id, { suggested_datetime: t.iso, tz_offset: tzOffset() }), `Snoozed to ${t.label.toLowerCase()}.`)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
            >
              <span className="w-[15px]" />{t.label}
            </button>
          ))}

          <div className="my-1 border-t border-[var(--line2)]" />
          {entry.owner === "me" ? (
            <button
              data-testid={`action-reassign-partner-${entry.id}`}
              onClick={() => run(() => updateEntry(entry.id, { owner: "partner" }), "Handed to your partner.")}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
            >
              <UserPlus size={15} /> Give to partner
            </button>
          ) : (
            <button
              data-testid={`action-reassign-me-${entry.id}`}
              onClick={() => run(() => updateEntry(entry.id, { owner: "me" }), "Took it back.")}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
            >
              <Undo2 size={15} /> Take it back
            </button>
          )}

          <button
            data-testid={`action-delete-${entry.id}`}
            onClick={() => { setOpen(false); deleteWithUndo(entry.id, { onDeleted: () => onUpdated && onUpdated(), onRestored: () => onUpdated && onUpdated() }); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-[var(--danger)] hover:bg-[var(--danger-soft)] transition-colors"
          >
            <Trash2 size={15} /> Delete
          </button>
        </div>
      )}

      <Sheet open={open && isMobile} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="bg-[var(--surface)] border-t border-[var(--line)] rounded-t-3xl px-3 pt-2 sheet-safe-bottom"
          data-testid={`item-actions-sheet-${entry.id}`}
        >
          <div className="mx-auto w-full max-w-md pb-1">
            <div className="h-1 w-10 rounded-full bg-[var(--line)] mx-auto mt-1.5 mb-3" />
            <p className="px-4 pb-2 text-sm text-[var(--ink3)] line-clamp-1">{entry.title || "This item"}</p>
            <div className="space-y-1">
              <button
                data-testid={`action-done-${entry.id}`}
                onClick={() => run(() => updateEntry(entry.id, { status: done ? "ACTIVE" : "DONE" }), done ? "Reopened." : "Done. One less thing to hold.")}
                className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] text-[var(--ink)] hover:bg-[var(--surface2)] transition-colors"
              >
                {done ? <Undo2 size={18} /> : <Check size={18} />}
                {done ? "Reopen" : "Mark done"}
              </button>

              <div className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-[var(--ink5)] flex items-center gap-1.5">
                <Clock size={12} /> Snooze
              </div>
              {targets.map((t) => (
                <button
                  key={t.key}
                  data-testid={`action-snooze-${t.key}-${entry.id}`}
                  onClick={() => run(() => updateEntry(entry.id, { suggested_datetime: t.iso, tz_offset: tzOffset() }), `Snoozed to ${t.label.toLowerCase()}.`)}
                  className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 min-h-[48px] text-[15px] text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
                >
                  <span className="w-[18px]" />{t.label}
                </button>
              ))}

              <div className="my-1 border-t border-[var(--line2)]" />
              {entry.owner === "me" ? (
                <button
                  data-testid={`action-reassign-partner-${entry.id}`}
                  onClick={() => run(() => updateEntry(entry.id, { owner: "partner" }), "Handed to your partner.")}
                  className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
                >
                  <UserPlus size={18} /> Give to partner
                </button>
              ) : (
                <button
                  data-testid={`action-reassign-me-${entry.id}`}
                  onClick={() => run(() => updateEntry(entry.id, { owner: "me" }), "Took it back.")}
                  className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
                >
                  <Undo2 size={18} /> Take it back
                </button>
              )}

              <button
                data-testid={`action-delete-${entry.id}`}
                onClick={() => { setOpen(false); deleteWithUndo(entry.id, { onDeleted: () => onUpdated && onUpdated(), onRestored: () => onUpdated && onUpdated() }); }}
                className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] text-[var(--danger)] hover:bg-[var(--danger-soft)] transition-colors"
              >
                <Trash2 size={18} /> Delete
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
